import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { ApiError, ApiClient } from '../api/client';
import { getSocketFactory } from '../api/socketFactory';
import type { WsServerMessage } from '../api/types';
import { GameSocket, type ConnectionState } from '../api/ws';
import { GuestSessionStore, type GuestSession } from '../auth/guestSession';
import { config } from '../config';

type MessageListener = (msg: WsServerMessage) => void;

export interface AppServices {
  api: ApiClient;
  store: GuestSessionStore;
  session: GuestSession | null;
  guestState: 'idle' | 'creating' | 'ready' | 'failed';
  guestError: string | null;
  retryGuest(): void;
  newGuest?(): Promise<void>;
  endSession?(): Promise<void>;
  login?(merge?: boolean): Promise<void>;
  socket: GameSocket | null;
  connection: ConnectionState;
  reconnectAttempt: number;
  onMessage(listener: MessageListener): () => void;
}

/**
 * ★ 2026-08-21 起 `export` —— 讓測試能**用真的 Provider**跑真的頁面。
 *
 * 在此之前這個 context 是模組私有的，於是想跑 `LobbyPage` / `useMatch` 的測試
 * 只剩下 `vi.mock('../app/AppContext')` 一條路 —— 而那會把 `useApp()` 本人
 * （含「不在 Provider 裡就 throw」那道守衛）換成一份替身。
 * 匯出一個 context 物件**不改變任何執行期行為**，卻讓受測程式碼從
 * `useApp()` 到 `useContext()` 整條都是真貨。
 *
 * ⚠️ 產品程式碼一律用 {@link useApp}，不要在頁面裡直接 `useContext(AppContext)` ——
 *   那會繞過那道守衛，把「忘了包 Provider」變成一個安靜的 `null`。
 */
export const AppContext = createContext<AppServices | null>(null);

export function AppProvider({ children }: { children: ReactNode }) {
  // store / api 只建立一次；它們沒有 React state，重建會弄丟 token。
  const storeRef = useRef<GuestSessionStore | null>(null);
  if (!storeRef.current) storeRef.current = new GuestSessionStore(config.storagePrefix);
  const store = storeRef.current;

  const apiRef = useRef<ApiClient | null>(null);
  if (!apiRef.current) {
    apiRef.current = new ApiClient({
      baseUrl: config.apiBaseUrl,
      getToken: () => store.getToken(),
      // C03 §5.1：只有換發那一個請求帶 cookie，其餘一律不帶（理由在 `client.ts`）。
      guestCookieExchange: config.guestPersistence === 'cookie',
    });
  }
  const api = apiRef.current;

  const [session, setSession] = useState<GuestSession | null>(() => store.get());
  const [guestState, setGuestState] = useState<AppServices['guestState']>(() => (store.get() ? 'ready' : 'idle'));
  const [guestError, setGuestError] = useState<string | null>(null);
  const [connection, setConnection] = useState<ConnectionState>('idle');
  const [reconnectAttempt, setReconnectAttempt] = useState(0);

  const listenersRef = useRef(new Set<MessageListener>());
  const socketRef = useRef<GameSocket | null>(null);
  const creatingRef = useRef(false);
  const stoppedKey = `${config.storagePrefix}session-ended`;
  const channelRef = useRef<BroadcastChannel | null>(null);
  const stopLocal = useCallback(() => {
    socketRef.current?.close();
    socketRef.current = null;
    sessionStorage.setItem(stoppedKey, '1');
    store.clear();
    setSession(null);
    setGuestState('idle');
  }, [store, stoppedKey]);

  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return;
    const channel = new BroadcastChannel(`${config.storagePrefix}identity`);
    channelRef.current = channel;
    channel.onmessage = () => stopLocal();
    return () => { channel.close(); channelRef.current = null; };
  }, [stopLocal]);

  const onMessage = useCallback((listener: MessageListener) => {
    listenersRef.current.add(listener);
    return () => {
      listenersRef.current.delete(listener);
    };
  }, []);

  /**
   * 匿名入場（C02 §3、C03 §5.1）。第一次進站就打 POST /auth/guest —— 玩家不需要按任何東西。
   * 這是「立即開玩，免註冊」的技術實現。
   *
   * ## 這個函式在 §5.1 之後的語意變了，但**程式碼一行都不用改**
   *
   * `POST /auth/guest` 在 `cookie` 模式下不再是「建一個新玩家」，而是「**換一張 token**」：
   * 瀏覽器自動附上 httpOnly cookie，後端據以回**同一個** `subject_id`。
   * 於是同一個瀏覽器的第二個分頁、明天再回來、F5 之後 sessionStorage 空了 ——
   * 全部都會換到同一個匿名身分，對局紀錄一路跟著。
   *
   * 前面那個 `if (store.get())` 的提早返回因此只是**省一次往返**，不再是身分的保存機制。
   *
   * ⚠️ 這件事**完全取決於後端**：後端沒發 cookie（或 CORS 沒開 credentials）時，
   * 這裡的行為與落地前一模一樣（每次換發都是新身分）—— 前端這一半自己看不出差別，
   * 這正是本 repo 反覆出現的那種接縫，所以驗收必須在**瀏覽器**裡看 `subject_id` 有沒有一樣。
   */
  const ensureGuest = useCallback(async () => {
    if (creatingRef.current || sessionStorage.getItem(stoppedKey) || location.pathname.endsWith('/auth/callback')) return;
    if (store.get()) {
      try {
        await api.whoami();
        setSession(store.get());
        setGuestState('ready');
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) stopLocal();
        else { setGuestError('無法確認身分，請稍後重試。'); setGuestState('failed'); }
      }
      return;
    }
    creatingRef.current = true;
    setGuestState('creating');
    setGuestError(null);
    try {
      const resp = await api.createGuest();
      const s = store.set(resp);
      setSession(s);
      setGuestState('ready');
    } catch (e) {
      setGuestState('failed');
      setGuestError(e instanceof Error ? e.message : String(e));
    } finally {
      creatingRef.current = false;
    }
  }, [api, store, stopLocal, stoppedKey]);

  useEffect(() => {
    void ensureGuest();
  }, [ensureGuest]);

  const endSession = useCallback(async () => {
    try {
      if (store.get()?.kind === 'player') {
        const { getUserManager } = await import('../auth/oidc');
        await getUserManager().signoutRedirect();
      } else if (store.getToken()) await api.endGuest();
      channelRef.current?.postMessage('ended');
      stopLocal();
    } catch (e) { setGuestError(e instanceof Error ? e.message : String(e)); throw e; }
  }, [api, store, stopLocal]);

  const newGuest = useCallback(async () => {
    if (creatingRef.current) return;
    creatingRef.current = true;
    try {
      if (store.get()?.kind === 'player') { await endSession(); return; }
      const response = await api.createGuest(undefined, true);
      channelRef.current?.postMessage('changed');
      socketRef.current?.close(); socketRef.current = null;
      sessionStorage.removeItem(stoppedKey);
      setSession(store.set(response)); setGuestState('ready'); setGuestError(null);
    } catch (e) { setGuestError(e instanceof Error ? e.message : String(e)); }
    finally { creatingRef.current = false; }
  }, [api, store, stoppedKey, endSession]);

  const login = useCallback(async (merge = false) => {
    try {
      const guest = store.get();
      if (!merge && guest?.kind !== 'player' && guest) await endSession();
      const { startLogin } = await import('../auth/identityActions');
      await startLogin(guest, merge);
    } catch (e) { setGuestError(e instanceof Error ? e.message : String(e)); }
  }, [store, endSession]);

  useEffect(() => {
    if (!config.oidcIssuer || !location.pathname.endsWith('/auth/callback')) return;
    if (creatingRef.current) return;
    creatingRef.current = true;
    void import('../auth/identityActions').then(async ({ finishLogin }) => {
      const { user, mergeError } = await finishLogin();
      const s = store.set({ guest_token: user.access_token, subject_id: user.profile.sub,
        display_name: user.profile.name ?? user.profile.sub,
        expires_at: new Date((user.expires_at ?? 0) * 1000).toISOString() }, 'player');
      sessionStorage.removeItem(stoppedKey);
      channelRef.current?.postMessage('changed');
      setSession(s); setGuestState('ready'); setGuestError(mergeError);
      window.history.replaceState(null, '', config.basePath);
      window.dispatchEvent(new PopStateEvent('popstate'));
    }).catch((e) => { setGuestError(String(e)); setGuestState('failed'); })
      .finally(() => { creatingRef.current = false; });
  }, [store, stoppedKey]);

  useEffect(() => {
    const check = () => {
      if (!store.getToken()) return;
      void api.whoami().catch((e) => {
        if (e instanceof ApiError && e.status === 401) stopLocal();
      });
    };
    window.addEventListener('focus', check);
    return () => window.removeEventListener('focus', check);
  }, [api, store, stopLocal]);

  // 有了 token 才建立 WebSocket（C02 §6：token 走 query string）。
  useEffect(() => {
    if (guestState !== 'ready' || !session) return;
    if (socketRef.current) return;

    const sock = new GameSocket(
      {
        url: config.wsUrl,
        getToken: () => store.getToken(),
        socketFactory: getSocketFactory(),
      },
      {
        onSessionEnded: stopLocal,
        onMessage: (msg) => {
          for (const fn of listenersRef.current) fn(msg);
        },
        onStateChange: (state, attempt) => {
          setConnection(state);
          setReconnectAttempt(attempt);
        },
      },
    );
    socketRef.current = sock;
    sock.connect();

    return () => {
      sock.close();
      socketRef.current = null;
    };
  }, [guestState, session, store]);

  const value = useMemo<AppServices>(
    () => ({
      api,
      newGuest, endSession, login,
      store,
      session,
      guestState,
      guestError,
      retryGuest: () => void ensureGuest(),
      socket: socketRef.current,
      connection,
      reconnectAttempt,
      onMessage,
    }),
    [api, store, session, guestState, guestError, ensureGuest, connection, reconnectAttempt, onMessage, newGuest, endSession, login],
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp(): AppServices {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp 必須放在 <AppProvider> 之內');
  return ctx;
}
